import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Alert,
  FormControlLabel,
  Checkbox,
  Typography
} from '@mui/material';
import instance from '../../utils/axiosConfig';
import { ThemeProvider } from '@mui/material/styles';
import menuTheme from '../../menuTheme';

function CreateModeratorForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    permissions: [
      'canVerifyUsers',
      'canManageInvoices',
      'canManageProducts',
      'canManageUserAccounts'
    ]
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');


    try {
      const response = await instance.post('/api/admin-management/moderators', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        permissions: formData.permissions // Send the permissions as an array of strings
      });
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Moderador creado exitosamente' });
        setFormData({
          name: '',
          email: '',
          password: '',
          permissions: [
            'canVerifyUsers',
            'canManageInvoices',
            'canManageProducts',
            'canManageUserAccounts'
          ]
        });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error al crear moderador');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permission) => {
    if (formData.permissions.includes(permission)) {
      setFormData(prevData => ({
        ...prevData,
        permissions: prevData.permissions.filter(p => p !== permission)
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        permissions: [...prevData.permissions, permission]
      }));
    }
  };

  return (
    <ThemeProvider theme={menuTheme}>
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ 
        maxWidth: 400,
        '& .MuiTextField-root': {
          mb: 2
        }
      }}
    >
      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ 
            mb: 3,
            borderRadius: '6px',
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          {message.text}
        </Alert>
      )}

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: '6px',
            '& .MuiAlert-message': {
              fontSize: '0.9rem'
            }
          }}
        >
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        variant="outlined"
        label="Nombre"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
          }
        }}
      />

      <TextField
        fullWidth
        variant="outlined"
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        required
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
          }
        }}
      />

      <TextField
        fullWidth
        variant="outlined"
        label="Contraseña"
        type="password"
        value={formData.password}
        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
        required
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
          }
        }}
      />

      <Box sx={{ 
        mt: 3, 
        mb: 2,
        '& .MuiFormControlLabel-root': {
          marginRight: 3
        }
      }}>
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 1.5,
            color: 'text.secondary',
            fontWeight: 500
          }}
        >
          Permisos del Moderador
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={formData.permissions.includes('canVerifyUsers')}
              onChange={() => handlePermissionChange('canVerifyUsers')}
              color="primary"
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main'
                }
              }}
            />
          }
          label="Verificar usuarios"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.9rem'
            }
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.permissions.includes('canManageInvoices')}
              onChange={() => handlePermissionChange('canManageInvoices')}
              color="primary"
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main'
                }
              }}
            />
          }
          label="Gestionar facturas"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.9rem'
            }
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.permissions.includes('canManageProducts')}
              onChange={() => handlePermissionChange('canManageProducts')}
              color="primary"
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main'
                }
              }}
            />
          }
          label="Gestionar productos"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.9rem'
            }
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.permissions.includes('canManageUserAccounts')}
              onChange={() => handlePermissionChange('canManageUserAccounts')}
              color="primary"
              sx={{
                '&.Mui-checked': {
                  color: 'primary.main'
                }
              }}
            />
          }
          label="Gestionar cuentas de usuario"
          sx={{
            '& .MuiFormControlLabel-label': {
              fontSize: '0.9rem'
            }
          }}
        />
      </Box>

      <Button 
        type="submit" 
        variant="contained" 
        color="primary"
        fullWidth
        sx={{ 
          mt: 2,
          py: 1.2,
          fontSize: '0.9rem',
          fontWeight: 500,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
          ...(loading && {
            backgroundColor: 'grey.500',
            '&:hover': {
              backgroundColor: 'grey.700',
            }
          })
        }}
        disabled={loading}
      >
        Crear Moderador
      </Button>
    </Box>
    </ThemeProvider>
  );
}

export default CreateModeratorForm; 